import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useCartStore, syncTrackingAttributesToCart } from "@/stores/cartStore";
import { useLanguage, isRTL } from "@/lib/i18n";
import { createBuyNowCart, formatPrice, CURRENCY_TO_COUNTRY, calcDiscountPercent } from "@/lib/shopify";
import { getFixedPrice, formatFixedPrice } from "@/lib/fixedPrices";
import { trackInitiateCheckout, generateEventId } from "@/lib/facebookPixel";
import { trackGA4InitiateCheckout } from "@/lib/googleAnalytics";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslatedProductContent } from "@/hooks/useTranslatedProductContent";
import { toast } from "sonner";
import { 
  ShoppingCart, 
  Loader2, 
  ChevronUp, 
  ChevronDown,
  Minus,
  Plus,
  CreditCard,
} from "lucide-react";


interface MobileStickyCartProps {
  product: {
    id: string;
    title: string;
    handle: string;
    variants: {
      edges: Array<{
        node: {
          id: string;
          title: string;
          price: { amount: string; currencyCode: string };
          compareAtPrice?: { amount: string; currencyCode: string } | null;
          availableForSale: boolean;
          selectedOptions: Array<{ name: string; value: string }>;
        };
      }>;
    };
  };
  handle: string;
}

export function MobileStickyCart({ product, handle }: MobileStickyCartProps) {
  const { t, currency, language } = useLanguage();
  const isMobile = useIsMobile();
  const addItem = useCartStore(state => state.addItem);
  const isLoading = useCartStore(state => state.isLoading);
  const productContent = useTranslatedProductContent(handle);
  const rtl = isRTL(language);
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [isBuyingNow, setIsBuyingNow] = useState(false);

  const variant = product.variants.edges[0]?.node;
  if (!variant) return null;

  const displayCurrency = currency || 'USD';
  const fp = getFixedPrice(product.handle, displayCurrency);
  const isOnSale = fp ? fp.original > fp.sale : false;
  const displayTitle = productContent.title || product.title;

  const handleAddToCart = async () => {
    await addItem({
      product: { node: product as any },
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity,
      selectedOptions: variant.selectedOptions || []
    });
  };

  const handleBuyNow = async () => {
    if (!variant || isBuyingNow) return;
    setIsBuyingNow(true);
    const newWindow = window.open('about:blank', '_blank');
    try {
      const result = await createBuyNowCart(variant.id, quantity, CURRENCY_TO_COUNTRY[currency] || 'US');
      if (result) {
        // Final sync of tracking attributes before checkout redirect
        await syncTrackingAttributesToCart(result.cartId);

        // Fire InitiateCheckout tracking (CAPI + GA4) before redirect
        const price = fp?.sale || parseFloat(variant.price.amount);
        const checkoutData = {
          items: [{ productId: product.id, variantId: variant.id, title: product.title, price, quantity }],
          totalValue: price * quantity,
          currency: displayCurrency,
        };
        // Generate ONE shared event_id for both FB and GA4 — ensures dashboard dedup
        const icEventId = await generateEventId('InitiateCheckout', Date.now().toString());
        await Promise.all([
          trackInitiateCheckout(checkoutData, icEventId),
          trackGA4InitiateCheckout(checkoutData, icEventId),
        ]);

        if (newWindow) {
          newWindow.location.href = result.checkoutUrl;
        } else {
          window.location.href = result.checkoutUrl;
        }
      } else {
        newWindow?.close();
        toast.error(t('cart.error') || 'Something went wrong');
      }
    } catch (err) {
      console.error('Buy Now error:', err);
      newWindow?.close();
      toast.error(t('cart.error') || 'Something went wrong');
    } finally {
      setIsBuyingNow(false);
    }
  };

  const discountPercent = fp ? calcDiscountPercent(fp.sale, fp.original) : 0;

  const priceDisplay = (
    <div className="flex items-center gap-2 flex-shrink-0">
      <span className="font-extrabold text-primary">
        {fp
          ? formatFixedPrice(fp.sale, displayCurrency)
          : formatPrice(variant.price.amount, variant.price.currencyCode, currency)
        }
      </span>
      {isOnSale && fp && (
        <span className="text-xs text-muted-foreground line-through">
          {formatFixedPrice(fp.original, displayCurrency)}
        </span>
      )}
      {isOnSale && discountPercent > 0 && !isMobile && (
        <Badge className="bg-destructive text-destructive-foreground border-0 text-[10px] px-1.5 py-0">
          -{discountPercent}%
        </Badge>
      )}
    </div>
  );

  const quantitySelector = (
    <div className="flex items-center border border-border rounded-lg bg-white">
      <Button
        variant="ghost"
        size="icon"
        className="h-9 w-9"
        onClick={(e) => {
          e.stopPropagation();
          setQuantity(Math.max(1, quantity - 1));
        }}
        disabled={quantity <= 1}
      >
        <Minus className="w-4 h-4" />
      </Button>
      <span className="w-10 text-center font-medium">{quantity}</span>
      <Button
        variant="ghost"
        size="icon"
        className="h-9 w-9"
        onClick={(e) => {
          e.stopPropagation();
          setQuantity(quantity + 1);
        }}
      >
        <Plus className="w-4 h-4" />
      </Button>
    </div>
  );

  // ── Desktop layout: single elegant row ──
  if (!isMobile) {
    return (
      <div
        className="fixed bottom-0 inset-x-0 z-40 hidden md:block"
        dir={rtl ? 'rtl' : 'ltr'}
      >
        <div className="bg-white/95 backdrop-blur-lg border-t border-primary/20 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
          <div className="max-w-screen-xl mx-auto px-6 py-3 flex items-center gap-6">
            {/* Product name */}
            <span className="font-semibold text-sm truncate min-w-0 flex-1 text-start">
              {displayTitle}
            </span>

            {/* Price */}
            {priceDisplay}

            {/* Separator */}
            <div className="w-px h-6 bg-border flex-shrink-0" />

            {/* Quantity */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-sm font-medium">{t('products.quantity')}:</span>
              {quantitySelector}
            </div>

            {/* Separator */}
            <div className="w-px h-6 bg-border flex-shrink-0" />

            {/* Action buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddToCart();
                }}
                disabled={isLoading || !variant.availableForSale}
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-5 py-2.5"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : !variant.availableForSale ? (
                  t('products.soldOut')
                ) : (
                  <>
                    <ShoppingCart className="w-4 h-4 me-2" />
                    {t('products.addToCart')}
                  </>
                )}
              </Button>

              <Button
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  handleBuyNow();
                }}
                disabled={isBuyingNow || !variant.availableForSale}
                className="border-primary text-primary hover:bg-primary/10 px-5 py-2.5"
              >
                {isBuyingNow ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 me-2" />
                    {t('products.buyNow')}
                  </>
                )}
              </Button>
            </div>

            {/* Terms agreement */}
            <p className="text-[10px] text-muted-foreground/70 text-center leading-tight mt-1">
              {t('product.termsAgreementBefore')} {t('product.termsLink')}{t('product.termsAgreementAfter') ? ` ${t('product.termsAgreementAfter')}` : ''}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Mobile layout: collapsible ──
  return (
    <div
      className="fixed bottom-0 inset-x-0 z-40 md:hidden"
      dir={rtl ? 'rtl' : 'ltr'}
    >
      <div className="bg-white/95 backdrop-blur-lg border-t border-primary/20 shadow-[0_-4px_20px_rgba(0,0,0,0.1)]">
        {/* Collapsed Header */}
        <div 
          className="flex items-center justify-between px-4 py-3 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-primary flex-shrink-0" />
            ) : (
              <ChevronUp className="w-5 h-5 text-primary flex-shrink-0" />
            )}
            <span className="font-medium text-sm truncate text-start">{displayTitle}</span>
          </div>
          <div className="flex-shrink-0 ms-2">
            {priceDisplay}
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="px-4 pb-4 space-y-3 border-t border-border/50">
            {/* Quantity Selector */}
            <div className="flex items-center justify-between pt-3">
              <span className="text-sm font-medium">{t('products.quantity')}:</span>
              {quantitySelector}
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddToCart();
                }}
                disabled={isLoading || !variant.availableForSale}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-5"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : !variant.availableForSale ? (
                  t('products.soldOut')
                ) : (
                  <>
                    <ShoppingCart className="w-5 h-5 me-2" />
                    {t('products.addToCart')}
                  </>
                )}
              </Button>

              <Button
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  handleBuyNow();
                }}
                disabled={isBuyingNow || !variant.availableForSale}
                className="w-full border-primary text-primary hover:bg-primary/10 py-5"
              >
                {isBuyingNow ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 me-2" />
                    {t('products.buyNow')}
                  </>
                )}
              </Button>
            </div>

            {/* Terms agreement */}
            <p className="text-[10px] text-muted-foreground/70 text-center leading-tight">
              {t('product.termsAgreementBefore')} {t('product.termsLink')}{t('product.termsAgreementAfter') ? ` ${t('product.termsAgreementAfter')}` : ''}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
